import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { unitTypeDefinition } from '../../../../entity-definitions/unit-type.definition';
import { unitDefinition } from '../../../../entity-definitions/unit.definition';
import { EntityDialogService } from '../../../../services/entity-dialog.service';
import { ToastService } from '../../../../services/toast.service';
import { UnitConversionDomainStore } from '../../../../stores/unit-conversion.store';
import { UnitTypeStore } from '../../../../stores/unit-type.store';
import { UnitStore } from '../../../../stores/unit.store';
import { UnitConversionDomain } from '../../../../types/unit-conversion.interface';
import { UnitType } from '../../../../types/unit-type.interface';
import { Unit } from '../../../../types/unit.interface';
import { GenericParentGridComponent } from '../../../generic/generic-parent-grid.component';
import { CesButtonComponent, CesMenuButtonComponent } from '../../../ui/ces-buttons';
import { CesIconComponent } from '../../../ui/ces-icon/ces-icon.component';
import { CesTitleComponent } from '../../../ui/ces-route-title/ces-route-title.component';

interface UnitFormResult {
  action: 'create' | 'edit' | 'delete';
  unit: Unit;
  unitConversionList: UnitConversionDomain[];
  deletedConversions: UnitConversionDomain[];
}

interface UnitTypeEditResult {
  __action: 'delete';
  entity: UnitType;
}

type UnitStoreApi = {
  entities(): Unit[];
  loadAll(): Promise<Unit[]>;
  create(entity: Unit): Promise<Unit | null>;
  update(id: number, changes: Partial<Unit>): Promise<Unit | null>;
  delete(id: number): Promise<void>;
};

type UnitTypeStoreApi = {
  entities(): UnitType[];
  loadAll(): Promise<UnitType[]>;
  create(entity: UnitType): Promise<UnitType | null>;
  update(id: number, changes: Partial<UnitType>): Promise<UnitType | null>;
  delete(id: number): Promise<void>;
};

type UnitConversionStoreApi = {
  create(entities: UnitConversionDomain[]): Promise<UnitConversionDomain[] | null>;
  delete(entity: UnitConversionDomain): Promise<UnitConversionDomain>;
};

@Component({
  selector: 'app-unit-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TabsModule,
    CesTitleComponent,
    GenericParentGridComponent,
    ButtonModule,
    CesButtonComponent,
    CesIconComponent,
    CesMenuButtonComponent,
    TranslocoModule,
  ],
  templateUrl: './unit-list.component.html',
  styleUrl: './unit-list.component.css',
})
export class UnitListComponent implements OnInit {
  protected readonly unitStore = inject(UnitStore) as unknown as UnitStoreApi;
  protected readonly unitTypeStore = inject(UnitTypeStore) as unknown as UnitTypeStoreApi;
  protected readonly unitConversionStore = inject(UnitConversionDomainStore) as unknown as UnitConversionStoreApi;

  private readonly dialogService = inject(EntityDialogService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);

  private readonly translations = toSignal(this.translateService.selectTranslation());

  unitDefinition = unitDefinition;
  unitTypeDefinition = unitTypeDefinition;

  units: Unit[] = [];
  unitTypes: UnitType[] = [];
  dataLoadingCompleted = false;
  dataLoadingCompletedUnitType = false;
  loadFailed = false;

  activeTab = '0';

  readonly items: Signal<MenuItem[]> = computed(() => {
    this.translations();
    return [
      { label: this.translateService.translate('entities.unit.messages.createUnit'), command: () => this.createUnit() },
      { label: this.translateService.translate('entities.unit.messages.createUnitType'), command: () => this.createUnitType() },
    ];
  });

  ngOnInit(): void {
    void this.loadData();
  }

  async loadData(): Promise<void> {
    this.loadFailed = false;
    try {
      await this.unitTypeStore.loadAll();
      await this.unitStore.loadAll();

      this.unitTypes = this.unitTypeStore.entities();
      this.units = this.sortByTypeThenName(this.unitStore.entities());
      this.dataLoadingCompleted = true;
      this.dataLoadingCompletedUnitType = true;
    } catch (error) {
      this.loadFailed = true;
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    } finally {
      this.cdr.markForCheck();
    }
  }

  // UAT 27.07 #21 : ordre d'affichage aligné sur les mesures — type d'unité (nom) puis unité (nom).
  private sortByTypeThenName(units: Unit[]): Unit[] {
    const typeNames = new Map(this.unitTypes.map(type => [type.id, type.name]));
    const typeName = (unit: Unit): string => (unit.unitTypeId === null ? '' : (typeNames.get(unit.unitTypeId) ?? ''));
    return [...units].sort((a, b) => typeName(a).localeCompare(typeName(b)) || a.name.localeCompare(b.name));
  }

  createUnit(): void {
    this.dialogService
      .openCreateDialog<Unit>({
        header: this.unitDefinition.messages.dialogTitles.create(),
        entityDefinition: this.unitDefinition,
        mode: 'create',
        size: 'medium',
        additionalData: {
          UnitType: this.unitTypes,
          Units: this.units,
        },
      })
      .subscribe(result => {
        if (!result) return;
        const unitResult = result as unknown as UnitFormResult;
        if (!unitResult.unit) return;

        void this.saveUnitCreation(unitResult);
      });
  }

  private async saveUnitCreation(unitResult: UnitFormResult): Promise<void> {
    try {
      const newUnit = await this.unitStore.create(unitResult.unit);
      if (!newUnit) return;

      if (unitResult.unitConversionList.length) {
        unitResult.unitConversionList.forEach(conversion => {
          conversion.srcUnitId = newUnit.id;
        });
        await this.unitConversionStore.create(unitResult.unitConversionList);
      }
      await this.loadData();
      this.activeTab = '0';
    } catch (error) {
      this.toastService.warning('common.toasts.saveFailed');
      console.error(this.translateService.translate('common.errors.saveFailed'), error);
    }
  }

  editUnit(unit: Unit): void {
    this.dialogService
      .openEditDialog<Unit>(unit, {
        header: this.unitDefinition.messages.dialogTitles.edit(),
        headerParams: { name: unit.name },
        entityDefinition: this.unitDefinition,
        mode: 'edit',
        size: 'medium',
        additionalData: {
          UnitType: this.unitTypeStore.entities(),
          Units: this.units,
        },
        onDelete: entity => this.deleteUnit(entity),
      })
      .subscribe(result => {
        if (!result) return;
        const unitResult = result as unknown as UnitFormResult;
        if (unitResult.action !== 'edit') return;

        void this.saveUnitEdition(unit.id, unitResult);
      });
  }

  private async saveUnitEdition(unitId: number, unitResult: UnitFormResult): Promise<void> {
    try {
      await this.unitStore.update(unitId, unitResult.unit);
      if (unitResult.unitConversionList.length) {
        await this.unitConversionStore.create(unitResult.unitConversionList);
      }
      await Promise.all(unitResult.deletedConversions.map(conversion => this.unitConversionStore.delete(conversion)));
      await this.loadData();
    } catch (error) {
      this.toastService.warning('common.toasts.saveFailed');
      console.error(this.translateService.translate('common.errors.saveFailed'), error);
    }
  }

  deleteUnit(unit: Unit): void {
    if (unit.isUsed) {
      this.toastService.warning('entities.unit.messages.deleteError');
      return;
    }

    this.unitStore
      .delete(unit.id)
      .then(() => {
        void this.loadData();
      })
      .catch((error: unknown) => {
        this.toastService.warning('entities.unit.messages.deleteError');
        console.error(this.translateService.translate('common.errors.unitDeletionFailed'), error);
      });
  }

  createUnitType(): void {
    this.dialogService
      .openCreateDialog<UnitType>({
        header: this.unitTypeDefinition.messages.dialogTitles.create(),
        entityDefinition: this.unitTypeDefinition,
        mode: 'create',
      })
      .subscribe(formData => {
        if (!formData) return;
        if ('__action' in formData && formData.__action === 'delete') return;

        void this.unitTypeStore
          .create(formData as UnitType)
          .then(() => this.loadData())
          .then(() => {
            this.activeTab = '1';
          })
          .catch(() => {
            console.error(this.translateService.translate('common.errors.unitTypeCreationFailed'));
          });
      });
  }

  editUnitType(type: UnitType): void {
    this.dialogService
      .openEditDialog<UnitType>(type, {
        header: this.unitTypeDefinition.messages.dialogTitles.edit(),
        headerParams: { name: type.name },
        entityDefinition: this.unitTypeDefinition,
        mode: 'edit',
        onDelete: entity => this.deleteUnitType(entity),
      })
      .subscribe(result => {
        if (!result) return;
        if ('__action' in result && result.__action === 'delete') {
          const deleteResult = result as UnitTypeEditResult;
          this.deleteUnitType(deleteResult.entity);
          return;
        }
        void this.unitTypeStore.update(type.id, result as Partial<UnitType>).then(() => {
          void this.loadData();
        });
      });
  }

  deleteUnitType(type: UnitType): void {
    if (type.hasUnit) {
      this.toastService.warning('entities.unitType.messages.deleteError');
      return;
    }

    this.unitTypeStore
      .delete(type.id)
      .then(() => {
        void this.loadData();
      })
      .catch((error: unknown) => {
        this.toastService.warning('entities.unitType.messages.deleteError');
        console.error(this.translateService.translate('common.errors.unitTypeDeletionFailed'), error);
      });
  }
}
